import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useAuthStore,
  useSaveUserSettings,
  useUploadUserLogo,
  useDeleteUserLogo,
} from "@iziping/common";
import { useNotification } from "../Notification/useNotification";
import { FileUploadInput } from "./FileUploadInput";
import { LanguageSelector } from "./LanguageSelector";
import styles from "./UserSettings.module.css";

const DEFAULT_PRIMARY = "#4f46e5";
const DEFAULT_SECONDARY = "#e19250";
const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/svg+xml",
];
const ACCEPTED_IMAGE_LABELS = ["JPG", "PNG", "WEBP", "SVG"];

interface UserSettingsPageProps {
  langs: readonly string[];
  onClose?: () => void;
}

export function UserSettingsPage({ langs, onClose }: UserSettingsPageProps) {
  const { t, i18n } = useTranslation();
  const { notify } = useNotification();
  const { user } = useAuthStore();

  const saveSettings = useSaveUserSettings();
  const uploadLogo = useUploadUserLogo();
  const deleteLogo = useDeleteUserLogo();

  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY);
  const [secondaryColor, setSecondaryColor] = useState(DEFAULT_SECONDARY);
  const [logoUrl, setLogoUrl] = useState<string | undefined>();

  useEffect(() => {
    if (user?.settings) {
      setPrimaryColor(user.settings.primaryColor ?? DEFAULT_PRIMARY);
      setSecondaryColor(user.settings.secondaryColor ?? DEFAULT_SECONDARY);
      setLogoUrl(user.settings.logoUrl);
    }
  }, [user?.settings]);

  const handleLanguageChange = async (lang: string) => {
    i18n.changeLanguage(lang);
    try {
      await saveSettings.mutateAsync({ language: lang });
    } catch {
      // changement local effectif, échec silencieux côté backend
    }
  };

  const handleSaveColors = async () => {
    try {
      await saveSettings.mutateAsync({
        primaryColor:
          primaryColor !== DEFAULT_PRIMARY ? primaryColor : undefined,
        secondaryColor:
          secondaryColor !== DEFAULT_SECONDARY ? secondaryColor : undefined,
      });
      notify(t("settings.appearance.save.success"), "success");
      onClose?.();
    } catch {
      notify(t("internal.server.error"), "error");
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await deleteLogo.mutateAsync();
      setLogoUrl(undefined);
    } catch {
      notify(t("internal.server.error"), "error");
    }
  };

  const content = (
    <div className={styles.settingsCard}>
      <h2 className={styles.pageTitle}>{t("settings.title")}</h2>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t("settings.language.title")}</h3>
        <LanguageSelector
          langs={langs}
          value={i18n.language}
          onChange={handleLanguageChange}
        />
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          {t("settings.appearance.title")}
        </h3>

        <FileUploadInput
          label={t("settings.appearance.logo")}
          acceptedMimeTypes={ACCEPTED_IMAGE_TYPES}
          acceptedFormatsLabel={ACCEPTED_IMAGE_LABELS}
          maxSizeMb={2}
          currentUrl={logoUrl}
          uploadFn={async (file) => {
            const result = await uploadLogo.mutateAsync(file);
            return result.url;
          }}
          onUpload={setLogoUrl}
          onRemove={handleRemoveLogo}
          onError={() => notify(t("internal.server.error"), "error")}
        />

        <div className={styles.colorGroup}>
          <span className={styles.colorLabel}>
            {t("settings.appearance.primaryColor")}
          </span>
          <div className={styles.colorRow}>
            <input
              type="color"
              className={styles.colorSwatch}
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
            />
            <span className={styles.colorHex}>{primaryColor}</span>
            {primaryColor !== DEFAULT_PRIMARY && (
              <button
                type="button"
                className={styles.colorResetBtn}
                onClick={() => setPrimaryColor(DEFAULT_PRIMARY)}
              >
                {t("settings.appearance.reset")}
              </button>
            )}
          </div>
        </div>

        <div className={styles.colorGroup}>
          <span className={styles.colorLabel}>
            {t("settings.appearance.secondaryColor")}
          </span>
          <div className={styles.colorRow}>
            <input
              type="color"
              className={styles.colorSwatch}
              value={secondaryColor}
              onChange={(e) => setSecondaryColor(e.target.value)}
            />
            <span className={styles.colorHex}>{secondaryColor}</span>
            {secondaryColor !== DEFAULT_SECONDARY && (
              <button
                type="button"
                className={styles.colorResetBtn}
                onClick={() => setSecondaryColor(DEFAULT_SECONDARY)}
              >
                {t("settings.appearance.reset")}
              </button>
            )}
          </div>
        </div>

        <button
          type="button"
          className="button"
          onClick={handleSaveColors}
          disabled={saveSettings.isPending}
        >
          {saveSettings.isPending
            ? "..."
            : t("settings.appearance.save.button")}
        </button>
      </section>
    </div>
  );

  return onClose ? (
    content
  ) : (
    <div className={styles.pageWrapper}>{content}</div>
  );
}
